/**
 * LAYKIN ET CIE — THEME JAVASCRIPT
 * Handles: header state, scroll reveals, product gallery, mobile nav, inquiry modal
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------
     1. HEADER — transparent on hero, solid on scroll
     ------------------------------------------------------------------ */
  const header = document.querySelector('.site-header');

  function initHeader() {
    if (!header) return;

    const isHomepage = document.body.classList.contains('template-index');

    if (isHomepage) {
      header.classList.add('site-header--transparent');

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            header.classList.add('site-header--transparent');
            header.classList.remove('site-header--solid');
          } else {
            header.classList.remove('site-header--transparent');
            header.classList.add('site-header--solid');
          }
        },
        { threshold: 0.1 }
      );

      const hero = document.querySelector('.hero');
      if (hero) observer.observe(hero);
    } else {
      header.classList.add('site-header--solid');
    }
  }

  /* ------------------------------------------------------------------
     2. SCROLL REVEALS — fade-up elements into view
     ------------------------------------------------------------------ */
  function initReveal() {
    const reveals = document.querySelectorAll('.reveal, .reveal-group');
    if (!reveals.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    reveals.forEach(el => observer.observe(el));
  }

  /* ------------------------------------------------------------------
     3. MOBILE NAV
     ------------------------------------------------------------------ */
  function initMobileNav() {
    const toggle = document.querySelector('.nav-toggle');
    const mobileNav = document.querySelector('.mobile-nav');
    const overlay = document.querySelector('.mobile-nav__overlay');
    const closeBtn = document.querySelector('.mobile-nav__close');

    if (!toggle || !mobileNav) return;

    function openNav() {
      mobileNav.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    function closeNav() {
      mobileNav.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    toggle.addEventListener('click', openNav);
    overlay?.addEventListener('click', closeNav);
    closeBtn?.addEventListener('click', closeNav);

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobileNav.classList.contains('is-open')) {
        closeNav();
      }
    });
  }

  /* ------------------------------------------------------------------
     4. PRODUCT GALLERY — thumbnail switching
     ------------------------------------------------------------------ */
  function initProductGallery() {
    const gallery = document.querySelector('.product-gallery');
    if (!gallery) return;

    const mainImage = gallery.querySelector('.product-gallery__main img');
    const thumbs = gallery.querySelectorAll('.product-gallery__thumb');

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.src;
        const srcset = thumb.dataset.srcset;
        const alt = thumb.dataset.alt;

        if (mainImage && src) {
          mainImage.style.opacity = '0';
          setTimeout(() => {
            mainImage.src = src;
            if (srcset) mainImage.srcset = srcset;
            if (alt) mainImage.alt = alt;
            mainImage.style.opacity = '1';
          }, 200);
        }

        // Active state
        thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });

    // Smooth opacity transition on gallery main
    if (mainImage) {
      mainImage.style.transition = 'opacity 200ms ease';
    }
  }

  /* ------------------------------------------------------------------
     5. VARIANT SELECTION
     ------------------------------------------------------------------ */
  function initVariants() {
    const variantGroups = document.querySelectorAll('.variant-options');

    variantGroups.forEach(group => {
      const buttons = group.querySelectorAll('.variant-btn');
      buttons.forEach(btn => {
        btn.addEventListener('click', () => {
          if (btn.classList.contains('is-unavailable')) return;
          buttons.forEach(b => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          // Update hidden variant input for Shopify
          const variantId = btn.dataset.variantId;
          const variantInput = document.querySelector('input[name="id"]');
          if (variantInput && variantId) {
            variantInput.value = variantId;
          }
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     6. INQUIRY MODAL
     ------------------------------------------------------------------ */
  function initInquiryModal() {
    const trigger = document.querySelector('.js-inquiry-trigger');
    const modal = document.querySelector('.inquiry-modal');
    const closeBtn = document.querySelector('.inquiry-modal__close');
    const overlay = document.querySelector('.inquiry-modal__overlay');

    if (!trigger || !modal) return;

    function openModal() {
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      modal.querySelector('input, textarea')?.focus();
    }

    function closeModal() {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    trigger.addEventListener('click', openModal);
    closeBtn?.addEventListener('click', closeModal);
    overlay?.addEventListener('click', closeModal);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });
  }

  /* ------------------------------------------------------------------
     7. CART DRAWER (lightweight AJAX cart)
     ------------------------------------------------------------------ */
  function initCart() {
    const addToCartForms = document.querySelectorAll('.js-add-to-cart');

    addToCartForms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const btn = form.querySelector('[type="submit"]');
        const originalText = btn.textContent;
        btn.textContent = 'Adding...';
        btn.disabled = true;

        const formData = new FormData(form);
        formData.append('sections', 'cart-count');

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: formData
          });

          if (res.ok) {
            // Update cart count
            const cartRes = await fetch('/cart.js');
            const cart = await cartRes.json();
            const countEl = document.querySelector('.cart-count');
            if (countEl) {
              countEl.textContent = cart.item_count;
              countEl.dataset.count = cart.item_count;
            }

            btn.textContent = 'Added';
            setTimeout(() => {
              btn.textContent = originalText;
              btn.disabled = false;
            }, 2000);
          } else {
            throw new Error('Failed to add');
          }
        } catch (err) {
          btn.textContent = 'Please try again';
          setTimeout(() => {
            btn.textContent = originalText;
            btn.disabled = false;
          }, 2000);
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     8. INIT ALL
     ------------------------------------------------------------------ */
  function init() {
    initHeader();
    initReveal();
    initMobileNav();
    initProductGallery();
    initVariants();
    initInquiryModal();
    initCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
